import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { setToken } from '../utils/tokenStorage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleSignInButton } from '../components/GoogleSignInButton';

export default function SignUp() {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    hasNumber: false,
    hasLetter: false
  });

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasLetter: /[a-zA-Z]/.test(password)
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    
    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username.trim() || formData.username.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        username: formData.username.trim(),
        password: formData.password,
        confirm_password: formData.confirm_password
      };
      if (formData.email.trim()) {
        payload.email = formData.email.trim().toLowerCase();
      }

      const response = await apiClient.post('/auth/register', payload);
      const userData = response.data;
      
      setToken(userData.session_token);
      
      setUser({
        user_id: userData.user_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        role: userData.role,
        username: userData.username
      });

      toast.success('Account created! Welcome to RazientSounds!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create account. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = formData.password && formData.confirm_password && formData.password === formData.confirm_password;
  const passwordsDontMatch = formData.password && formData.confirm_password && formData.password !== formData.confirm_password;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <Card className="w-full max-w-md glass-default border-white/10" data-testid="signup-card">
        <CardHeader className="text-center pb-2">
          <h1 className="text-3xl font-bold gradient-text">Create Account</h1>
          <p className="text-white/60 mt-2">Join RazientSounds today</p>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3" data-testid="signup-error">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white flex items-center gap-2">
                <User size={16} />
                Username
              </Label>
              <Input
                id="username"
                name="username"
                type="text"
                placeholder="Choose a username"
                value={formData.username}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-violet-500 h-12"
                data-testid="signup-username-input"
                required
              />
              <p className="text-xs text-white/40">Letters, numbers, and underscores only</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white flex items-center gap-2">
                <Mail size={16} />
                Email <span className="text-white/40 text-xs font-normal">(optional)</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-violet-500 h-12"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white flex items-center gap-2">
                <Lock size={16} />
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 focus:border-violet-500 h-12 pr-12"
                  data-testid="signup-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {formData.password && (
                <div className="space-y-1 mt-2">
                  <div className={`flex items-center gap-2 text-xs ${passwordStrength.length ? 'text-green-400' : 'text-white/50'}`}>
                    {passwordStrength.length ? <CheckCircle size={14} /> : <div className="w-3.5 h-3.5 rounded-full border border-white/30" />}
                    At least 8 characters
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${passwordStrength.hasLetter ? 'text-green-400' : 'text-white/50'}`}>
                    {passwordStrength.hasLetter ? <CheckCircle size={14} /> : <div className="w-3.5 h-3.5 rounded-full border border-white/30" />}
                    Contains a letter
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${passwordStrength.hasNumber ? 'text-green-400' : 'text-white/50'}`}>
                    {passwordStrength.hasNumber ? <CheckCircle size={14} /> : <div className="w-3.5 h-3.5 rounded-full border border-white/30" />}
                    Contains a number
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-white flex items-center gap-2">
                <Lock size={16} />
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`bg-white/5 border-white/10 focus:border-violet-500 h-12 pr-12 ${
                    passwordsMatch ? 'border-green-500/50' : ''
                  } ${passwordsDontMatch ? 'border-red-500/50' : ''}`}
                  data-testid="signup-confirm-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {passwordsMatch && (
                <p className="text-green-400 text-xs flex items-center gap-1">
                  <CheckCircle size={14} /> Passwords match
                </p>
              )}
              {passwordsDontMatch && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle size={14} /> Passwords do not match
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-lg font-bold rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <GoogleSignInButton label="Sign up with Google" />
        </CardContent>

        <CardFooter className="flex flex-col gap-4 pt-0">
          <p className="text-xs text-white/50 text-center">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-violet-400 hover:underline">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-violet-400 hover:underline">Privacy Policy</Link>
          </p>
          
          <div className="text-center">
            <p className="text-white/60">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-400 hover:underline font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
